import {
  SyncProfileData,
  SyncProfileDataAddressTypeEnum
} from '@slr/solution1-sdk';
import {
  useGetCurrentUserProfileWithSolution1,
  useUpdateUserProfileAndSync
} from 'hooks';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { profileFormResolver, ProfileFormValues } from '../forms.schema';

interface AddressFormProps {
  onSuccess?: () => void;
  isRegistration?: boolean;
  hasPendingInvitation?: boolean;
}

export const AddressForm: React.FC<AddressFormProps> = ({
  onSuccess,
  isRegistration = false,
  hasPendingInvitation = false
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'profilePage'
  });

  const { data: currentUserProfile } = useGetCurrentUserProfileWithSolution1();
  const { mutate: updateUserProfileAndSync, isPending: isUpdating } =
    useUpdateUserProfileAndSync({
      onSuccess: onSuccess,
      isRegistration,
      hasPendingInvitation
    });

  const addressForm = useForm<ProfileFormValues>({
    mode: 'onChange',
    resolver: profileFormResolver,
    defaultValues: {
      accountType: 'private',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      companyName: '',
      taxNumber: ''
    }
  });

  const accountType = addressForm.watch('accountType');

  const savedAccountType = currentUserProfile?.billingAddress?.addressType;
  const isAccountTypeLocked =
    savedAccountType === 'private' || savedAccountType === 'business';

  React.useEffect(() => {
    if (currentUserProfile?.billingAddress) {
      const accountType =
        currentUserProfile.billingAddress.addressType === 'business'
          ? 'business'
          : 'private';
      addressForm.reset({
        accountType: accountType,
        street: currentUserProfile.billingAddress.street || '',
        houseNumber: currentUserProfile.billingAddress.houseNumber || '',
        postalCode: currentUserProfile.billingAddress.postalCode || '',
        city: currentUserProfile.billingAddress.city || '',
        companyName: currentUserProfile.billingAddress.companyName || '',
        taxNumber: currentUserProfile.billingAddress.taxNumber || ''
      });
    }
  }, [currentUserProfile, addressForm]);

  const onSubmitAddress = (data: ProfileFormValues) => {
    const effectiveAccountType = isAccountTypeLocked
      ? savedAccountType === 'business'
        ? 'business'
        : 'private'
      : data.accountType;

    let name: string | null = null;
    if (effectiveAccountType === 'private') {
      if (currentUserProfile?.firstName && currentUserProfile?.lastName) {
        name = `${currentUserProfile.firstName} ${currentUserProfile.lastName}`;
      }
    }

    const syncProfileData: SyncProfileData = {
      street: data.street || '',
      houseNumber: data.houseNumber || '',
      postalCode: data.postalCode || '',
      city: data.city || '',
      companyName:
        effectiveAccountType === 'business'
          ? (data.companyName || '').trim() || null
          : null,
      name: name,
      addressType:
        effectiveAccountType === 'business'
          ? SyncProfileDataAddressTypeEnum.Business
          : SyncProfileDataAddressTypeEnum.Private,
      taxNumber:
        effectiveAccountType === 'business'
          ? (data.taxNumber || '').trim() || null
          : null
    };

    updateUserProfileAndSync(syncProfileData);
  };

  return (
    <FormProvider {...addressForm}>
      <form onSubmit={addressForm.handleSubmit(onSubmitAddress)}>
        <div className="mt-5">
          <h2 className="register-section-title">{t('forms.account.title')}</h2>
          <Row>
            <Col xs={12} md={6}>
              <div className="mb-3">
                <label className="form-label" htmlFor="accountTypePrivate" style={{ whiteSpace: 'nowrap' }}>
                  {t('forms.account.fields.accountType.label')} *
                </label>
                <div className="form-check">
                  <input
                    className={`form-check-input ${addressForm.formState.errors.accountType ? 'is-invalid' : ''}`}
                    type="radio"
                    id="accountTypePrivate"
                    value="private"
                    disabled={isAccountTypeLocked}
                    {...addressForm.register('accountType')}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="accountTypePrivate"
                  >
                    {t('forms.account.fields.accountType.options.private')}
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className={`form-check-input ${addressForm.formState.errors.accountType ? 'is-invalid' : ''}`}
                    type="radio"
                    id="accountTypeBusiness"
                    value="business"
                    disabled={isAccountTypeLocked}
                    {...addressForm.register('accountType')}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="accountTypeBusiness"
                  >
                    {t('forms.account.fields.accountType.options.business')}
                  </label>
                </div>
                {addressForm.formState.errors.accountType && (
                  <div className="invalid-feedback">
                    {addressForm.formState.errors.accountType.message}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>

        {accountType === 'business' && (
          <div className="mt-4">
            <h2 className="register-section-title">
              {isRegistration
                ? t('forms.address.formTitle')
                : t('forms.address.businessSectionTitle')}
            </h2>
            {!isRegistration && (
              <p className="text-muted mb-4">{t('forms.address.billingHint')}</p>
            )}
            <Row>
              <Col xs={12}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="companyName">
                    {t('forms.address.companyName')} *
                  </label>
                  <input
                    className={`form-control form-control-lg ${addressForm.formState.errors.companyName ? 'is-invalid' : ''}`}
                    type="text"
                    id="companyName"
                    {...addressForm.register('companyName')}
                  />
                  {addressForm.formState.errors.companyName && (
                    <div className="invalid-feedback d-block">
                      {addressForm.formState.errors.companyName.message}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={12} md={8}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="taxNumber">
                    {t('forms.address.vatNumber')}
                  </label>
                  <input
                    className={`form-control form-control-lg ${addressForm.formState.errors.taxNumber ? 'is-invalid' : ''}`}
                    type="text"
                    id="taxNumber"
                    {...addressForm.register('taxNumber')}
                  />
                  {addressForm.formState.errors.taxNumber && (
                    <div className="invalid-feedback d-block">
                      {addressForm.formState.errors.taxNumber.message}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        )}

        <div className="mt-4">
          {!(isRegistration && accountType === 'business') && (
            <h2 className="register-section-title">
              {t('forms.address.formTitle')}
            </h2>
          )}
          <Row>
            <Col xs={12} md={8}>
              <div className="mb-3">
                <label className="form-label" htmlFor="street">
                  {t('forms.address.formFieldLabels.street')} *
                </label>
                <input
                  className={`form-control form-control-lg ${addressForm.formState.errors.street ? 'is-invalid' : ''}`}
                  type="text"
                  id="street"
                  {...addressForm.register('street')}
                />
                {addressForm.formState.errors.street && (
                  <div className="invalid-feedback d-block">
                    {addressForm.formState.errors.street.message}
                  </div>
                )}
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="mb-3">
                <label className="form-label" htmlFor="houseNumber" style={{ whiteSpace: 'nowrap' }}>
                  {t('forms.address.formFieldLabels.houseNumber')} *
                </label>
                <input
                  className={`form-control form-control-lg ${addressForm.formState.errors.houseNumber ? 'is-invalid' : ''}`}
                  type="text"
                  id="houseNumber"
                  {...addressForm.register('houseNumber')}
                />
                {addressForm.formState.errors.houseNumber && (
                  <div className="invalid-feedback d-block">
                    {addressForm.formState.errors.houseNumber.message}
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={4}>
              <div className="mb-3">
                <label className="form-label" htmlFor="postalCode" style={{ whiteSpace: 'nowrap' }}>
                  {t('forms.address.formFieldLabels.postCode')} *
                </label>
                <input
                  className={`form-control form-control-lg ${addressForm.formState.errors.postalCode ? 'is-invalid' : ''}`}
                  type="text"
                  id="postalCode"
                  inputMode="numeric"
                  {...addressForm.register('postalCode')}
                />
                {addressForm.formState.errors.postalCode && (
                  <div className="invalid-feedback d-block">
                    {addressForm.formState.errors.postalCode.message}
                  </div>
                )}
              </div>
            </Col>
            <Col>
              <div className="mb-3">
                <label className="form-label" htmlFor="city" style={{ whiteSpace: 'nowrap' }}>
                  {t('forms.address.formFieldLabels.city')} *
                </label>
                <input
                  className={`form-control form-control-lg ${addressForm.formState.errors.city ? 'is-invalid' : ''}`}
                  type="text"
                  id="city"
                  {...addressForm.register('city')}
                />
                {addressForm.formState.errors.city && (
                  <div className="invalid-feedback d-block">
                    {addressForm.formState.errors.city.message}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
        <div className="d-flex justify-content-end mt-3">
          <Button
            size="lg"
            variant="primary"
            type="submit"
            disabled={isUpdating}
          >
            {isUpdating
              ? t('forms.address.saving')
              : t('forms.address.formSubmitButtonLabel')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
